import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // timestamp
}

/** Session duration: keep login for 24 hours. */
export const SESSION_EXPIRY_MINUTES = 24 * 60; // 1440

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly TOKEN_EXPIRY_KEY = 'tokenExpiry';
  
  private tokenSubject = new BehaviorSubject<string | null>(this.getAccessToken());
  public token$: Observable<string | null> = this.tokenSubject.asObservable();

  constructor() {
    // Check token expiry on initialization
    this.checkTokenExpiry();
  }

  setTokens(accessToken: string, refreshToken: string, expiresInMinutes: number = SESSION_EXPIRY_MINUTES): void {
    // Always use 24h session so user stays logged in for SESSION_EXPIRY_MINUTES
    const expiresAt = Date.now() + (SESSION_EXPIRY_MINUTES * 60 * 1000);
    
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt.toString());
    
    this.tokenSubject.next(accessToken);
  }

  getAccessToken(): string | null {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (token && !this.isTokenExpired()) {
      return token;
    }
    return null;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return true;
    }
    return Date.now() >= parseInt(expiry, 10);
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null && !this.isTokenExpired();
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    this.tokenSubject.next(null);
  }

  /** Clear tokens and all app data from localStorage (use on logout). */
  clearAll(): void {
    this.clearTokens();
    localStorage.clear();
    sessionStorage.clear();
  }

  private checkTokenExpiry(): void {
    if (this.isTokenExpired() && this.getAccessToken()) {
      this.clearTokens();
    }
  }

  updateAccessToken(accessToken: string, _expiresInMinutes: number = SESSION_EXPIRY_MINUTES): void {
    const expiresAt = Date.now() + (SESSION_EXPIRY_MINUTES * 60 * 1000);
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt.toString());
    this.tokenSubject.next(accessToken);
  }
}

